// Shared HTML5 drag helpers for "move row to a different room" interaction
// in ROOMS view. Used by Row.jsx (scheduled) and FloatingTaskRow.jsx (floating).
//
// Payloads on dragstart (a row emits BOTH MIMEs simultaneously so different
// drop targets can consume different MIMEs):
//   • application/x-ps-room-move  → RoomBand drop target (move to other room)
//   • application/x-ps-task-group → other Row drop target (group with task)
//
// The room-move payload shape:
//   { kind: "room-move", block_ids: string[], task_ids: string[] }
//
// The task-group payload shape:
//   { kind: "task-group", task_ids: string[], block_ids: string[] }
//   — Row's drop handler treats the dropped payload as the SOURCE set; the
//     target task id is the row that received the drop, appended LAST so the
//     backend treats it as the drop target.

export const ROOM_MOVE_MIME = "application/x-ps-room-move";
export const TASK_GROUP_MIME = "application/x-ps-task-group";
export const SUBTASK_SOURCE_MIME = "application/x-ps-subtask-source";

/**
 * Build a selection-aware payload.
 * @param {Object} args
 * @param {string} args.rowKey            this row's selection key
 *                                        (block.id or `ft:${task.id}`)
 * @param {string[]} args.selection       current grid selection
 * @returns {{block_ids:string[], task_ids:string[]}}
 */
export function buildRoomMovePayload({ rowKey, selection }) {
  const inSelection = selection?.includes(rowKey);
  const keys = inSelection && selection.length > 1 ? selection : [rowKey];
  const block_ids = [];
  const task_ids = [];
  for (const k of keys) {
    if (k.startsWith("ft:")) task_ids.push(k.slice(3));
    else block_ids.push(k);
  }
  return { kind: "room-move", block_ids, task_ids };
}

/** Total item count for a payload (used in the drag image badge). */
export function payloadCount(payload) {
  return (payload.block_ids?.length || 0) + (payload.task_ids?.length || 0);
}

/**
 * Attach a count-badge drag image when dragging more than one row.
 * The DOM node is created off-screen, used as the drag image, then removed
 * on the next animation frame (browser snapshots it synchronously).
 */
export function setRoomMoveDragImage(dataTransfer, count) {
  if (count <= 1) return;
  const ghost = document.createElement("div");
  ghost.style.cssText =
    "position:absolute;top:-9999px;left:-9999px;padding:6px 10px;background:#1a1410;color:#fbbf24;border:1px solid #fbbf24;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,monospace;font-size:11px;letter-spacing:.12em;text-transform:uppercase;border-radius:2px;white-space:nowrap;";
  ghost.textContent = `↦ × ${count} TO ROOM`;
  document.body.appendChild(ghost);
  dataTransfer.setDragImage(ghost, 16, 16);
  requestAnimationFrame(() => ghost.remove());
}

/**
 * Resolve the *task ids* a row-move payload refers to. block_ids are looked
 * up against the blocks-by-id map; floating task ids pass through.
 */
export function payloadToTaskIds(payload, blockById) {
  const ids = [];
  for (const bid of payload.block_ids || []) {
    const b = blockById?.[bid];
    if (b?.task_id) ids.push(b.task_id);
  }
  for (const tid of payload.task_ids || []) ids.push(tid);
  return ids;
}

/**
 * One-stop dragstart handler factory. Returns the function suitable for
 * `onDragStart`. Sets BOTH MIME types (room-move + task-group), sets the
 * drag image, and is robust to React-state batching: if `getSelection` is
 * provided it is read AT DRAG TIME (so the latest selection is always honoured).
 *
 * Pass `isSubtask: true` (or `getIsSubtask`) when the dragged row is a child
 * of a parent group — an additional `application/x-ps-subtask-source` MIME
 * is emitted, which the ScheduleGrid wrapper consumes when the drop lands
 * on whitespace ⇒ unparent the subtask.
 */
export function makeRoomMoveDragStart({ rowKey, selection, getSelection, isSubtask, getIsSubtask }) {
  return (e) => {
    const sel = typeof getSelection === "function" ? getSelection() : selection;
    const payload = buildRoomMovePayload({ rowKey, selection: sel || [] });
    const json = JSON.stringify(payload);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(ROOM_MOVE_MIME, json);
    e.dataTransfer.setData(TASK_GROUP_MIME, JSON.stringify({ ...payload, kind: "task-group" }));
    const sub = typeof getIsSubtask === "function" ? getIsSubtask() : isSubtask;
    if (sub) {
      e.dataTransfer.setData(SUBTASK_SOURCE_MIME, JSON.stringify({ kind: "subtask-source", ...payload }));
    }
    setRoomMoveDragImage(e.dataTransfer, payloadCount(payload));
  };
}
